import { metadata, withMetadata } from "../common/mod.js";
import { iterable } from "./iterable.js";
import { encodeHexPrefixed } from "./mod.js";
import { tuple } from "./tuple.js";
export function map($key, $value) {
    return withMetadata(metadata("$.map", map, $key, $value), iterable({
        $el: tuple($key, $value),
        calcLength: (map) => map.size,
        rehydrate: (values) => new ShapeMap($key, values),
        assert(assert) {
            assert.instanceof(this, ShapeMap);
        },
    }));
}
export function set($value) {
    return withMetadata(metadata("$.set", set, $value), iterable({
        $el: $value,
        calcLength: (set) => set.size,
        rehydrate: (values) => new ShapeSet($value, values),
        assert(assert) {
            assert.instanceof(this, ShapeSet);
        },
    }));
}
export class ShapeMap {
    $key;
    #inner = new Map();
    #hexMemo = new WeakMap();
    constructor($key, entries) {
        this.$key = $key;
        if (entries) {
            for (const [key, value] of entries) {
                this.set(key, value);
            }
        }
    }
    #transformKey(key) {
        return transformKey(this.$key, this.#hexMemo, key);
    }
    get size() {
        return this.#inner.size;
    }
    get [Symbol.toStringTag]() {
        return "ShapeMap";
    }
    clear() {
        this.#inner.clear();
    }
    delete(key) {
        return this.#inner.delete(this.#transformKey(key));
    }
    forEach(callbackfn, thisArg) {
        this.#inner.forEach(([key, value]) => callbackfn.call(thisArg, value, key, this));
    }
    get(key) {
        return this.#inner.get(this.#transformKey(key))?.[1];
    }
    has(key) {
        return this.#inner.has(this.#transformKey(key));
    }
    set(key, value) {
        this.#inner.set(this.#transformKey(key), [key, value]);
        return this;
    }
    [Symbol.iterator]() {
        return this.#inner.values();
    }
    entries() {
        return this.#inner.values();
    }
    *keys() {
        for (const { 0: key } of this) {
            yield key;
        }
    }
    *values() {
        for (const { 1: value } of this) {
            yield value;
        }
    }
}
export class ShapeSet {
    $value;
    #inner = new Map();
    #hexMemo = new WeakMap();
    constructor($value, values) {
        this.$value = $value;
        if (values) {
            for (const value of values) {
                this.add(value);
            }
        }
    }
    #transformValue(value) {
        return transformKey(this.$value, this.#hexMemo, value);
    }
    get size() {
        return this.#inner.size;
    }
    get [Symbol.toStringTag]() {
        return "ShapeSet";
    }
    clear() {
        this.#inner.clear();
    }
    delete(value) {
        return this.#inner.delete(this.#transformValue(value));
    }
    forEach(callbackfn, thisArg) {
        this.#inner.forEach((value) => callbackfn.call(thisArg, value, value, this));
    }
    has(key) {
        return this.#inner.has(this.#transformValue(key));
    }
    add(value) {
        this.#inner.set(this.#transformValue(value), value);
        return this;
    }
    [Symbol.iterator]() {
        return this.#inner.values();
    }
    *entries() {
        for (const value of this) {
            yield [value, value];
        }
    }
    keys() {
        return this.#inner.values();
    }
    values() {
        return this.#inner.values();
    }
}
function transformKey($key, hexMemo, key) {
    if (typeof key === "string") {
        // This ensures that the hexes won't ever clash with regular string keys,
        // but leaves most string keys unchanged for performance
        return key.startsWith("0x") ? "0x" + key : key;
    }
    if (typeof key !== "object" && typeof key !== "function" || !key) {
        return key;
    }
    const existingHex = hexMemo.get(key);
    if (existingHex)
        return existingHex;
    const hex = encodeHexPrefixed($key.encode(key));
    hexMemo.set(key, hex);
    return hex;
}
//# sourceMappingURL=collections.js.map